import { Button, Checkbox, Modal, Panel, Row, Typography } from "matsuri-ui"
import { useAuth } from "m2m-components/storage/useM2mAuth"
import { useCallback, useMemo, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import styled from "@emotion/styled"
import { css } from "@emotion/react"
import CloseIcon from "@mui/icons-material/Close"
import { useMobileLayoutProps } from "@/hooks/useMobileLayoutProps"
import { MobileAppHeader } from "@/components/Header/MobileAppHeader"
import { Header } from "@/components/Header/Header"
import { assertIsDefined } from "@/utils/assetIsDefined"
import { genCleaningReportPagePath } from "@/routes/protected"
import { useCheckItems } from "@/hooks/useCheckItems"
import { BackToDetailConfirmDialog } from "@/components/Dialog/BackToDetailConfirmDialog"
import { useNavigateBackToDetail } from "@/hooks/useNavigateBackToDetail"
import { useTranslation } from "@/hooks/useTranslation"
import { nextPathAfterCheckItems } from "@/pages/CleaningReportPage/routes/nextPath"

const Footer = styled.footer`
    padding: 16px 16px 34px 16px;
`
interface listItemType {
    id: string
    label: string
    disabled: boolean
}

const CheckItemsList = ({
    bodyProps,
    ...props
}: {
    items: { id: string; content: string }[]
    bodyProps: {
        style: {
            paddingTop: number
            paddingBottom: number
        }
    }
    onNext: () => void
}) => {
    const { t } = useTranslation()
    const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set())
    const items = useMemo(
        () =>
            props.items.map(({ id, content }) => ({
                id,
                label: content,
                disabled: checkedIds.has(id)
            })),
        [props.items, checkedIds]
    )

    const handleChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const target = event.currentTarget
            const id = target.name

            setCheckedIds(prev => {
                const next = new Set(prev)
                if (target.checked) {
                    next.add(id)
                } else {
                    next.delete(id)
                }
                return next
            })
        },
        []
    )

    const allChecked = useMemo(() => {
        return !Object.values(items).some(item => !item.disabled)
    }, [items])

    const [openNotCheckDialog, setOpenNotCheckDialog] = useState<boolean>(false)
    const notCheckMessage = t(
        "チェックしていないアイテムがあります。本当に次に進みますか？"
    )

    const handleModalClose = useCallback(() => {
        setOpenNotCheckDialog(false)
    }, [])

    const handleNextDialogClicked = useCallback(() => {
        if (allChecked) {
            props.onNext()
            handleModalClose()
        } else {
            setOpenNotCheckDialog(true)
        }
    }, [allChecked, handleModalClose, props])

    const handleNext = useCallback(() => {
        handleModalClose()
        props.onNext()
    }, [handleModalClose, props])

    return (
        <>
            <div {...bodyProps}>
                {items.map(item => {
                    const { label, disabled, id } = item

                    return (
                        <Panel
                            as="label"
                            disabled={disabled}
                            key={id}
                            style={{ cursor: "pointer" }}
                            icon={
                                <Checkbox name={id} onChange={handleChange} />
                            }
                        >
                            {label}
                        </Panel>
                    )
                })}
                <Footer>
                    <Button
                        variant="filled"
                        color={allChecked ? "primary" : undefined}
                        fullWidth
                        onClick={handleNextDialogClicked}
                    >
                        {t("次へ")}
                    </Button>
                </Footer>
            </div>
            <Modal
                width={"calc(100% - 32px)"}
                backdrop
                open={openNotCheckDialog}
                body={<Typography>{notCheckMessage}</Typography>}
                footer={
                    <Row gap={1} justify={"space-around"}>
                        <Button variant={"filled"} onClick={handleModalClose}>
                            {t("キャンセル")}
                        </Button>
                        <Button
                            color={"error"}
                            variant={"filled"}
                            onClick={handleNext}
                        >
                            {t("次へ")}
                        </Button>
                    </Row>
                }
            />
        </>
    )
}

const ItemsPerPage = 10

export const CheckItemsPage = () => {
    const { t } = useTranslation()
    const { cleaningId } = useParams<"cleaningId">()

    assertIsDefined(cleaningId)

    const { token } = useAuth()
    const { data: checkitems } = useCheckItems(token, cleaningId)

    // 清掃詳細画面に戻る確認モーダル
    const {
        confirmBackOpen,
        openConfirmBack,
        closeConfirmBack,
        goBackToDetail
    } = useNavigateBackToDetail("cleaning", cleaningId)
    const items = useMemo(() => {
        // SUPPRESS https://github.com/matsuri-tech/m2m-cleaning-cleaner-front/issues/96
        return (checkitems?.items || []).map((item, index) => ({
            id: index.toString(),
            content: item.title
        }))
    }, [checkitems])

    const { rootProps, bodyProps } = useMobileLayoutProps()
    const paged = useMemo(() => {
        const paged: { id: string; content: string }[][] = []
        let current: { id: string; content: string }[] = []
        for (const item of items) {
            current.push(item)

            if (current.length === ItemsPerPage) {
                paged.push(current)
                current = []
            }
        }
        if (current.length > 0) {
            paged.push(current)
        }

        return paged
    }, [items])
    const [currentPage, setCurrentPage] = useState(0)

    const navigate = useNavigate()

    const handleNext = useCallback(() => {
        if (currentPage + 1 >= paged.length) {
            navigate(
                genCleaningReportPagePath(cleaningId, nextPathAfterCheckItems())
            )
        } else {
            setCurrentPage(p => p + 1)
        }
    }, [cleaningId, currentPage, navigate, paged.length])

    return (
        <>
            <main {...rootProps}>
                <Header
                    css={css`
                        padding-top: env(safe-area-inset-top, 0px);
                    `}
                >
                    <MobileAppHeader
                        title={
                            <Typography variant="h4">
                                {t("清掃チェックリスト")} ({currentPage + 1} /{" "}
                                {paged.length})
                            </Typography>
                        }
                        onNext={openConfirmBack}
                        nextIcon={<CloseIcon />}
                    />
                </Header>
                <CheckItemsList
                    items={paged[currentPage] ?? []}
                    bodyProps={bodyProps}
                    onNext={handleNext}
                />
            </main>

            {/* 清掃詳細画面に戻る確認モーダル */}
            <BackToDetailConfirmDialog
                type="cleaning"
                open={confirmBackOpen}
                onClose={closeConfirmBack}
                onConfirm={goBackToDetail}
            />
        </>
    )
}
